import type { SaveData } from "./models";

export type PlatformContext = "desktop" | "mobile" | "pwa" | "android" | "ios";

export interface ControlCopy {
  title: string;
  body: string;
  hints: string[];
}

export const CURRENT_APP_VERSION = "1.0.0";

export const FEATURE_DISCOVERY: Record<string, { title: string; body: string }> = {
  shield_diamond: { title: "Shield Diamond", body: "Blocks one hazard hit." },
  clean_diamond: { title: "Clean Diamond", body: "Removes one nearby hazard." },
  freeze_diamond: { title: "Freeze Diamond", body: "Pauses security hazards for 3 seconds." },
  time_diamond: { title: "Time Diamond", body: "Adds time, but raises Security pressure faster." },
  security_heat: { title: "Security Heat", body: "Higher Heat means more danger and better rewards." },
  relics: { title: "Relics", body: "Master Vault relics give small permanent bonuses." },
  endless_rush: { title: "Endless Rush", body: "Unlocked after Level 10. Survive as long as possible." },
  tracking_drone: { title: "Tracking Drone", body: "Follows your position. Keep moving." },
  hunter_laser: { title: "Hunter Laser", body: "Warns first, then fires ahead of your path." },
  pulse_mine: { title: "Pulse Mine", body: "Flashes before it explodes." },
  jammer_field: { title: "Jammer Field", body: "Reduces pickup reach and combo timing." },
  continue_run_ad: {
    title: "Continue Run",
    body: "Watch a short ad after a hazard hit or timeout to keep the run alive, once per run.",
  },
  double_gold_ad: {
    title: "Double Gold",
    body: "Watch a short ad after a successful run to double the gold you just earned.",
  },
};

export const CHAPTER_INTROS = [
  {
    id: "chapter_intro_street",
    title: "Street Safes",
    body: "Learn the basics. Move fast, collect diamonds, escape clean.",
  },
  {
    id: "chapter_intro_bank",
    title: "Bank Vaults",
    body: "Security gets smarter. Watch for drones and stronger locks.",
  },
  {
    id: "chapter_intro_casino",
    title: "Casino Floor",
    body: "High risk, high reward. Rare diamonds can change everything.",
  },
  {
    id: "chapter_intro_museum",
    title: "Museum Heist",
    body: "Precision matters. Hazards punish careless routes.",
  },
  {
    id: "chapter_intro_server",
    title: "Server Vault",
    body: "Digital security interferes with your timing and reach.",
  },
  {
    id: "chapter_intro_ghost",
    title: "Ghost Vault",
    body: "Reality bends. Expect every security system at once.",
  },
];

export function controlCopyFor(platform: PlatformContext): ControlCopy {
  if (platform === "desktop") {
    return {
      title: "MOVE",
      body: "Use WASD, Arrow Keys, or Mouse Drag. Try moving now.",
      hints: ["W A S D", "↑ ← ↓ →", "Click and drag to move"],
    };
  }
  return {
    title: "MOVE",
    body: "Drag anywhere on the screen to move. Try moving now.",
    hints: ["Drag Anywhere", "One thumb", "Keep your route clear"],
  };
}

export function shouldShowOnboarding(save: SaveData): boolean {
  return !save.onboardingCompleted && !save.onboardingSkipped;
}

export function shouldShowWhatsNew(save: SaveData, version = CURRENT_APP_VERSION): boolean {
  return !shouldShowOnboarding(save) && save.lastSeenWhatsNewVersion !== version;
}

export function featureAlreadySeen(save: SaveData, featureId: string): boolean {
  return save.discoveredFeatures.includes(featureId);
}
